/* eslint-disable arrow-body-style */
import React, { useMemo } from 'react';
import classnames from 'classnames';
import { Table, TableProps, TableColumn, TableAddon } from 'tea-component';
import { getRecordKeyOfMockable } from '@gp/utils/common';
import { getHasGRMilestoneFinancialsForecastData } from '@gp/pages/Pipeline/ProjectDetails/Milestone/utils';
import { getForecastColumns } from './utils';

import type { IBaseProps } from '@home/types/common';
import type { IMilestoneFinancialForecastVO } from '@gp/types/milestones';

import './Table.scss';
import './ForecastTable.scss';

interface IForecastTableProps extends IBaseProps, Omit<TableProps, 'columns'> {
  isEdit?: boolean;
  value?: IMilestoneFinancialForecastVO[];
  onChange?: (payload: IMilestoneFinancialForecastVO[]) => void;
}

const { autotip } = Table.addons;

const ForecastTable: React.FC<IForecastTableProps> = (props) => {
  const { style, className, isEdit = false, value: records, onChange: updateRecords, ...restProps } = props;

  const addons = useMemo<TableAddon<IMilestoneFinancialForecastVO>[]>(
    () => [
      autotip({
        emptyText: (
          <div className="dbg-key-milestones-financials-table__no-data-wrapper">
            Forecast Financial data has not been updated at the current milestone.
          </div>
        ),
      }),
    ],
    [],
  );

  const reviewRecords = useMemo<IMilestoneFinancialForecastVO[]>(() => {
    if (isEdit) return records as IMilestoneFinancialForecastVO[];
    const hasGRMilestoneFinancialsForecastData = getHasGRMilestoneFinancialsForecastData(records);
    return hasGRMilestoneFinancialsForecastData ? (records as IMilestoneFinancialForecastVO[]) : [];
  }, [isEdit, records]);

  const columns = useMemo<TableColumn<IMilestoneFinancialForecastVO>[]>(() => {
    return getForecastColumns({
      isEdit,
      onChange: (cvm, key, value) => {
        const { mock_id: mockId } = cvm || {};
        const recordIdx = records!.findIndex((record) => record?.mock_id === mockId);
        const record = records![recordIdx];
        const recordNew = { ...record, [key]: value };
        const recordsNew = [...records!.slice(0, recordIdx), recordNew, ...records!.slice(recordIdx + 1)];
        updateRecords?.(recordsNew);
      },
    });
  }, [isEdit, records, updateRecords]);

  return (
    <div
      style={style}
      className={classnames(
        'dbg-key-milestones-financials-table dbg-key-milestones-financials-forecast-table',
        className,
        { 'is-review': !isEdit, 'is-edit': isEdit },
      )}
    >
      <Table
        {...restProps}
        disableHoverHighlight
        style={restProps?.tableStyle}
        className={restProps?.tableClassName}
        recordKey={getRecordKeyOfMockable}
        records={reviewRecords}
        addons={addons}
        columns={columns}
      />
    </div>
  );
};

export default ForecastTable;
